import { observer } from 'mobx-react-lite';
import { useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { SidebarFooterRow } from '@/components/blocks/Layout/Sidebar/SidebarFooterRow';
import { ModalsManager } from '@/components/modals/modalsManager';
import { YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { changelogSeenStore } from './changelog-seen.store';
import { type ChangelogEntry } from './changelog.feed';
import {
  useChangelogEntryPageQuery,
  useRecentUnseenChangelogEntry,
} from './changelog.query';
import { ChangelogDismissMenu } from './ChangelogDismissMenu';
import { ChangelogEntryModal } from './ChangelogEntryModal';
import { useIsTruncated } from './use-is-truncated';
import './changelog-row.css';

export const ChangelogSidebarRow = observer(() => {
  const entry = useRecentUnseenChangelogEntry();
  return entry && !changelogSeenStore.isSidebarHidden ? (
    <UnseenChangelogRow entry={entry} />
  ) : null;
});

const UnseenChangelogRow = observer(({ entry }: { entry: ChangelogEntry }) => {
  const { data: preview } = useChangelogEntryPageQuery(entry);
  const labelRef = useRef<HTMLSpanElement>(null);
  const isTitleTruncated = useIsTruncated(labelRef);
  const shouldAskOnDismiss = changelogSeenStore.shouldAskOnDismiss;

  return (
    <SidebarFooterRow
      className="group/changelog"
      dismissLabel="Dismiss what's new"
      icon={<Icons.megaphone className="size-3.5 text-shell-accent" />}
      label="New Features"
      labelClassName={
        isTitleTruncated
          ? cn(
              'transition-opacity duration-100 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
              'group-hover/changelog:opacity-0 group-hover/changelog:delay-150',
              'group-focus-within/changelog:opacity-0 group-focus-within/changelog:delay-150',
              'group-has-[[data-state=open]]/changelog:opacity-0'
            )
          : undefined
      }
      labelRef={labelRef}
      onClick={() => {
        trackEvent('CHANGELOG_SIDEBAR_CLICKED');
        changelogSeenStore.markOpened(entry.id);
        ModalsManager.show(ChangelogEntryModal, { entry }, { unique: true });
      }}
      trailing={
        shouldAskOnDismiss ? <ChangelogDismissMenu entry={entry} /> : undefined
      }
      onDismiss={
        shouldAskOnDismiss
          ? undefined
          : () => {
              trackEvent('CHANGELOG_SIDEBAR_DISMISSED');
              changelogSeenStore.dismiss(entry.id);
            }
      }
    >
      <span aria-hidden className="changelog-trail" />
      <YStack
        className={cn(
          'h-0 overflow-hidden opacity-0 [interpolate-size:allow-keywords]',
          'transition-[height,opacity] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]',
          'group-hover/changelog:h-auto group-hover/changelog:opacity-100 group-hover/changelog:duration-200 group-hover/changelog:delay-150',
          'group-focus-within/changelog:h-auto group-focus-within/changelog:opacity-100 group-focus-within/changelog:duration-200',
          'group-has-[[data-state=open]]/changelog:h-auto group-has-[[data-state=open]]/changelog:opacity-100',
          'motion-reduce:transition-none'
        )}
      >
        <YStack className="gap-2 px-1 pt-1 pb-1">
          <span className="mx-1 text-foreground">{entry.title}</span>
          {preview?.imageUrl ? (
            <img
              src={preview.imageUrl}
              alt=""
              className="aspect-video w-full rounded-lg object-cover"
            />
          ) : null}
          {preview?.description ? (
            <span className="mx-1 mb-0.5 text-xs text-foreground-muted line-clamp-3">
              {preview.description}
            </span>
          ) : null}
        </YStack>
      </YStack>
    </SidebarFooterRow>
  );
});
